import React, { useEffect } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Loader from 'src/presentation/components/atoms/Loader';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNotificationStore } from 'src/domain/stores/useNotificationStore';
import { useAuthenticationStore } from 'src/domain/stores/useAuthenticationStore';
import Colors from 'src/core/utils/Colors';
import Constants from 'src/core/utils/Constants';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import { navigatePage, replacePage } from 'src/presentation/helpers/NavigationHelper';

function SplashScreen() {
	const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
	const authStore = useAuthenticationStore();

	const navigateTo = (
		ID_path: string | number,
		notificationOrigin: string,
		event_uuid?: string,
		notification_id?: string,
	) => {
		const navStore = useNavigationStore.getState();
		const pageIDs = typeof ID_path === 'string' ? ID_path.split('/') : Array.of(ID_path.toString());
		const page = useEventStore.getState().getPageByID(pageIDs);

		if (page !== null && typeof page.type === 'string') {
			useEventStore.getState().updateTheme(page.event_uuid);
			navStore.setActiveEventByUuid(page.event_uuid);
			navStore.setNotificationOrigin(notificationOrigin);
			navigation.replace(Constants.screenIndex);
			const extra = Platform.OS === 'ios' ? undefined : { fromOutsideApp: true };
			navigatePage(navigation, page.type, page, extra);
		} else {
			const pageId = pageIDs.slice(-1)[0] ?? '';
			useEventStore.getState().updateTheme(event_uuid ?? '');
			navStore.setActiveEventByUuid(event_uuid ?? '');
			navStore.setNotificationOrigin(notificationOrigin);
			useEventStore
				.getState()
				.getPage({ eventUuid: event_uuid ?? '', pageId })
				.then((data) => {
					if (notification_id) useNotificationStore.getState().opened(notification_id);
					const extra = Platform.OS === 'ios' ? undefined : { fromOutsideApp: true };
					if (data.type) navigatePage(navigation, data.type, data, extra);
				})
				.catch(() => {
					navigation.navigate(Constants.screenIndex);
				});
		}
	};

	useEffect(() => {
		// URL-based deep-links (e.g.

		if (useNavigationStore.getState().fromNotification) {
			setTimeout(() => {
				replacePage(navigation, authStore.initialRoute);
			}, 1000);
		}

		const unsubscribe = useNotificationStore.getState().onNotificationOpened((remoteMessage) => {
			// Only the legacy page push (data.url, never a deeplink) is handled here. The event
			// deeplink push (data.uri, the reminder) is routed centrally in Navigation, so exclude
			// it explicitly and only set the replace-page flag for a real page push.
			if (
				remoteMessage.data?.url &&
				!remoteMessage.data?.uri &&
				remoteMessage.from &&
				remoteMessage.data?.notification_id
			) {
				useNavigationStore.getState().fromNotification = true;
				navigateTo(
					String(remoteMessage.data.url),
					remoteMessage.from,
					remoteMessage.data.event_uuid ? String(remoteMessage.data.event_uuid) : undefined,
					String(remoteMessage.data.notification_id),
				);
			}
		});

		return unsubscribe;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<View style={styles.root}>
			<Loader bgColor={Colors.primary} />
		</View>
	);
}

export default SplashScreen;

const styles = StyleSheet.create({
	root: {
		flex: 1,
		backgroundColor: Colors.primary,
	},
});
